import { Pencil, Trash2, Package, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// Loading Skeleton
const ProductSkeleton = () => (
  <div className="h-24 skeleton-pulse rounded-lg"></div>
);

// Empty State
const EmptyState = ({ onAdd }) => (
  <Card>
    <CardContent className="py-12 text-center">
      <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
      <h3 className="font-semibold mb-2">No products yet</h3>
      <p className="text-muted-foreground mb-4">Add your first product to get started</p>
      <Button onClick={() => onAdd()} data-testid="add-first-product">
        <Plus className="w-4 h-4 mr-2" />
        Add Product
      </Button>
    </CardContent>
  </Card>
);

// Product Item
const ProductItem = ({ product, onEdit, onDelete }) => (
  <Card className={product.product_type === 'physical' ? 'product-card-physical' : 'product-card-affiliate'}>
    <CardContent className="p-4">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-md overflow-hidden bg-muted shrink-0">
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold truncate" style={{ fontFamily: 'Archivo, sans-serif' }}>
              {product.name}
            </h3>
            {product.featured && (
              <Badge variant="secondary" className="text-xs">Featured</Badge>
            )}
          </div>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span>${product.price.toFixed(2)}</span>
            <span>•</span>
            <span>{product.category}</span>
            <span>•</span>
            <Badge className={product.product_type === 'physical' ? 'badge-physical' : 'badge-affiliate'}>
              {product.product_type}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="icon"
            onClick={() => onEdit(product)}
            data-testid={`edit-product-${product.id}`}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="text-destructive hover:text-destructive"
            onClick={() => onDelete(product.id)}
            data-testid={`delete-product-${product.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

const ProductList = ({ products, loading, onEdit, onDelete }) => {
  if (loading) {
    return (
      <div className="grid gap-4">
        {[1, 2, 3].map((i) => <ProductSkeleton key={`skeleton-${i}`} />)}
      </div>
    );
  }

  if (products.length === 0) {
    return <EmptyState onAdd={onEdit} />;
  }

  return (
    <div className="space-y-3" data-testid="products-list">
      {products.map((product) => (
        <ProductItem 
          key={product.id} 
          product={product} 
          onEdit={onEdit} 
          onDelete={onDelete} 
        />
      ))}
    </div>
  );
};

export default ProductList;
